import { plannedLengthMs } from '../resolveSession'
import type { CannedScenarioInfo, ScenarioSession } from '../types'
import {
  BLANK_BOARD_ID,
  BLANK_EXERCISE_ID,
  BLANK_WALKTHROUGH_ID,
  createBlankBoardSession,
  createBlankExerciseSession,
  createBlankWalkthroughSession,
} from './blankBoard'
import { EAGLE_MOUNTAIN_INCIDENT_ID, createEagleMountainIncidentSession } from './eagleMountainIncident'
import {
  EXAMPLE_WHITEBOARD_ID,
  createExampleWhiteboardSession,
} from './exampleWhiteboard'
import { MIRACLE_HUDSON_ID, createMiracleHudsonSession } from './miracleHudson'
import { YUCCA_DIG_IN_ID, createYuccaDigInSession } from './yuccaDigIn'
import {
  YUCCA_FIRE_EVAC_ID,
  YUCCA_GAS_LEAK_ID,
  YUCCA_ROAD_CLOSURE_ID,
  createFireEvacuationSession,
  createPoliceRoadClosureSession,
  createResidentialGasLeakSession,
} from './yuccaSlices'

export {
  BLANK_BOARD_ID,
  BLANK_EXERCISE_ID,
  BLANK_LIBRARY_TEMPLATE_IDS,
  BLANK_WALKTHROUGH_ID,
  blankLibraryIdForType,
} from './blankBoard'
export { EAGLE_MOUNTAIN_INCIDENT_ID } from './eagleMountainIncident'
export { EXAMPLE_WHITEBOARD_ID } from './exampleWhiteboard'
export { MIRACLE_HUDSON_ID } from './miracleHudson'
export { YUCCA_DIG_IN_ID } from './yuccaDigIn'
export { YUCCA_FIRE_EVAC_ID, YUCCA_GAS_LEAK_ID, YUCCA_ROAD_CLOSURE_ID } from './yuccaSlices'

/** The four boards that are one incident: three slices, then the full story. */
const YUCCA_ELM_TEMPLATE_IDS = new Set<string>([
  YUCCA_GAS_LEAK_ID,
  YUCCA_ROAD_CLOSURE_ID,
  YUCCA_FIRE_EVAC_ID,
  YUCCA_DIG_IN_ID,
])

export function isYuccaElmTemplate(id: string | undefined | null): boolean {
  return id != null && YUCCA_ELM_TEMPLATE_IDS.has(id)
}

const HISTORICAL_TEMPLATE_IDS = new Set<string>([MIRACLE_HUDSON_ID])

export function isHistoricalTemplate(id: string | undefined | null): boolean {
  return id != null && HISTORICAL_TEMPLATE_IDS.has(id)
}

/** Card hint only. Copies keep the template title and do not include this phrase. */
export function yuccaElmCardLead(id: string | undefined | null): string | null {
  if (id !== YUCCA_DIG_IN_ID) return null
  return 'Full story'
}

function cannedDurationMs(create: () => ScenarioSession): number {
  const session = create()
  // Only an authored end counts as a duration; never invent a 1 hr default for cards.
  return plannedLengthMs(session) ?? 0
}

export const CANNED_SCENARIOS: CannedScenarioInfo[] = [
  {
    id: EXAMPLE_WHITEBOARD_ID,
    title: 'Example whiteboard',
    summary: 'Kitchen-sink whiteboard: hazard, command, zones, response.',
    durationMs: cannedDurationMs(createExampleWhiteboardSession),
    mapLabel: 'Bare geography',
    create: createExampleWhiteboardSession,
    // Kept for generation / wizard recovery; not listed on Templates.
    hiddenFromTemplates: true,
  },
  {
    id: BLANK_BOARD_ID,
    title: 'Untitled whiteboard',
    summary: '',
    durationMs: cannedDurationMs(createBlankBoardSession),
    mapLabel: 'Streets',
    create: createBlankBoardSession,
  },
  {
    id: BLANK_WALKTHROUGH_ID,
    title: 'Untitled walkthrough',
    summary: '',
    durationMs: cannedDurationMs(createBlankWalkthroughSession),
    mapLabel: 'Streets',
    create: createBlankWalkthroughSession,
  },
  {
    id: BLANK_EXERCISE_ID,
    title: 'Untitled exercise',
    summary: '',
    durationMs: cannedDurationMs(createBlankExerciseSession),
    mapLabel: 'Streets',
    create: createBlankExerciseSession,
  },
  {
    id: YUCCA_GAS_LEAK_ID,
    title: 'Residential gas leak',
    summary: 'Gas dig-in at Yucca & Elm — leak, Engine 1 on scene.',
    durationMs: cannedDurationMs(createResidentialGasLeakSession),
    mapLabel: 'Eagle Mountain',
    create: createResidentialGasLeakSession,
  },
  {
    id: YUCCA_ROAD_CLOSURE_ID,
    title: 'Police response and road closure',
    summary: 'Road closes at Elm, Yucca, and Eagle Mountain Blvd.',
    durationMs: cannedDurationMs(createPoliceRoadClosureSession),
    mapLabel: 'Eagle Mountain',
    create: createPoliceRoadClosureSession,
  },
  {
    id: YUCCA_FIRE_EVAC_ID,
    title: 'Fire escalation and structure evacuation',
    summary: 'Fire grows along Yucca; school evacuation.',
    durationMs: cannedDurationMs(createFireEvacuationSession),
    mapLabel: 'Eagle Mountain',
    create: createFireEvacuationSession,
  },
  {
    id: YUCCA_DIG_IN_ID,
    title: 'Backhoe hits gas line at Yucca & Elm',
    summary: 'Full dig-in script — FD, closes, ignition, school evac (~28 min).',
    // Match create() — hose redeploy can snap the board past the 28 min story window.
    durationMs: cannedDurationMs(createYuccaDigInSession),
    mapLabel: 'Eagle Mountain',
    create: createYuccaDigInSession,
  },
  {
    id: MIRACLE_HUDSON_ID,
    title: 'Miracle on the Hudson',
    summary: 'Flight 1549 — bird strike, Hudson ditch, all 155 survive.',
    durationMs: cannedDurationMs(createMiracleHudsonSession),
    mapLabel: 'Streets',
    create: createMiracleHudsonSession,
  },
  {
    id: EAGLE_MOUNTAIN_INCIDENT_ID,
    title: 'Eagle Mountain cascade',
    summary: 'Compounding injects near Eagle Mountain — Kaiser Rd truck, leak, pipeburst, road close, school fire, wind, EMS.',
    durationMs: cannedDurationMs(createEagleMountainIncidentSession),
    mapLabel: 'Eagle Mountain',
    create: createEagleMountainIncidentSession,
    hiddenFromTemplates: true,
  },
]

export function visibleCannedScenarios(): CannedScenarioInfo[] {
  return CANNED_SCENARIOS.filter((s) => !s.hiddenFromTemplates)
}

export function defaultCannedId(): string {
  return BLANK_BOARD_ID
}

export function cannedById(id: string): CannedScenarioInfo {
  return (
    CANNED_SCENARIOS.find((s) => s.id === id) ??
    CANNED_SCENARIOS.find((s) => s.id === BLANK_BOARD_ID) ??
    CANNED_SCENARIOS[0]!
  )
}
